import {Alert, Button, Col, Row, Select, Space, Tooltip} from "antd";
import {QuestionCircleOutlined} from "@ant-design/icons";
import {useEffect, useMemo, useState} from "react";
import styled from "styled-components";
import {StyledCustomersDataBox} from "../../../../../styles/customers/customers";
import {useGetSubscriptionSalesInsightsApi} from "../../../../../react-query-hooks/store/subscriptions/get-subscription-sales-insights";

/** Total revenue — stretch to match sibling height; typography/wrap rules avoid overflow on narrow columns */
const StyledInsightsRevenueCard = styled(StyledCustomersDataBox)`
  flex: 1 1 auto;
  width: 100%;
  height: auto;
  box-sizing: border-box;
  min-height: 13rem;

  .data-box-title {
    min-width: 0;
    line-height: 1.35;
  }

  .data-box-value {
    max-width: 100%;
    overflow-wrap: anywhere;
    word-break: break-word;
    line-height: 1.25;
    hyphens: auto;
  }

  > .data-circle {
    pointer-events: none;
  }
`;

/** Total sales — stretched to same column height as revenue; standard KPI typography */
const StyledInsightsSalesCard = styled(StyledCustomersDataBox)`
  flex: 1 1 auto;
  width: 100%;
  height: auto;
  box-sizing: border-box;
  min-height: 13rem;

  > .data-circle {
    pointer-events: none;
  }
`;

type SubscriptionSalesAndInsightsRevenueSectionProps = {
  subscriptionId: number;
  defaultCurrency?: string;
  /** From GET subscription when sales insights omit `totalRevenueByCurrency` or currencies are empty. */
  fallbackTotalRevenue?: string;
};

export function SubscriptionSalesAndInsightsRevenueSection({
  subscriptionId,
  defaultCurrency,
  fallbackTotalRevenue,
}: SubscriptionSalesAndInsightsRevenueSectionProps) {
  const [selectedCurrency, setSelectedCurrency] = useState<string>("");

  const insightsQuery = useGetSubscriptionSalesInsightsApi({subscriptionId});

  const revenueByCurrency = useMemo(
    () => insightsQuery.data?.totalRevenueByCurrency ?? {},
    [insightsQuery.data]
  );

  const currencies = useMemo(
    () => Object.keys(revenueByCurrency).sort(),
    [revenueByCurrency]
  );

  const currencyOptions = useMemo(
    () => currencies.map((code) => ({label: code, value: code})),
    [currencies]
  );

  useEffect(() => {
    setSelectedCurrency("");
  }, [subscriptionId]);

  useEffect(() => {
    if (currencies.length === 0) {
      return;
    }
    if (selectedCurrency && currencies.includes(selectedCurrency)) {
      return;
    }
    const matchDefault =
      defaultCurrency?.trim() &&
      currencies.find((c) => c.toUpperCase() === defaultCurrency.trim().toUpperCase());
    setSelectedCurrency(matchDefault ?? currencies[0]);
  }, [currencies, defaultCurrency, selectedCurrency]);

  const fallbackAmount = Number(fallbackTotalRevenue ?? 0);
  const fallbackFormatted = Number.isFinite(fallbackAmount)
    ? fallbackAmount.toLocaleString()
    : "0";

  const selectedAmount =
    selectedCurrency && currencies.includes(selectedCurrency)
      ? Number(revenueByCurrency[selectedCurrency] ?? 0)
      : undefined;

  const revenueTooltip =
    currencies.length > 1
      ? "Select a currency to view subscription revenue for that currency."
      : "Your total subscription revenue is calculated per currency from billing analytics.";

  if (insightsQuery.isLoading) {
    return (
      <div style={{marginTop: "2.4rem"}}>
        <Space size={16} direction="vertical" style={{display: "flex"}}>
          Loading…
        </Space>
      </div>
    );
  }

  if (insightsQuery.isError) {
    return (
      <div style={{marginTop: "2.4rem"}}>
        <Alert type="error" message={insightsQuery.error.message} />
      </div>
    );
  }

  const totalSales =
    insightsQuery.data?.totalSales !== undefined &&
    Number.isFinite(insightsQuery.data.totalSales)
      ? insightsQuery.data.totalSales
      : 0;

  return (
    <div style={{marginTop: "2.4rem"}}>
      <Space size={16} direction="vertical" style={{display: "flex"}}>
        <Row gutter={[16, 20]} align="stretch">
          <Col xs={24} md={12} style={{display: "flex", flexDirection: "column"}}>
            <StyledInsightsRevenueCard
              style={{
                backgroundColor: "#EAE6F3",
                color: "#272037",
                borderRadius: "5px",
                border: "1.6px solid #43375D",
              }}
            >
              <div style={{backgroundColor: "#B3A8CD"}} className="data-circle"></div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                  columnGap: 10,
                  rowGap: 8,
                  marginBottom: 4,
                }}
              >
                <p className="data-box-title" style={{margin: 0, flex: "1 1 12rem"}}>
                  Total revenue{" "}
                  <Tooltip placement="left" title={revenueTooltip}>
                    <Button icon={<QuestionCircleOutlined />} size="small" type="text" />
                  </Tooltip>
                </p>
                {currencyOptions.length > 1 ? (
                  <Select
                    size="small"
                    value={selectedCurrency || undefined}
                    onChange={setSelectedCurrency}
                    options={currencyOptions}
                    style={{width: "min(92px, 100%)", flexShrink: 0}}
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : null}
              </div>
              {currencies.length > 1 ? (
                <h2 className="data-box-value" style={{margin: 0}}>
                  {selectedCurrency && selectedAmount !== undefined
                    ? `${selectedCurrency} ${selectedAmount.toLocaleString()}`
                    : "—"}
                </h2>
              ) : currencies.length === 1 ? (
                <h2 className="data-box-value" style={{margin: 0}}>
                  {`${currencies[0]} ${Number(
                    revenueByCurrency[currencies[0]] ?? 0
                  ).toLocaleString()}`}
                </h2>
              ) : (
                <h2 className="data-box-value" style={{margin: 0}}>{`${
                  defaultCurrency ?? ""
                } ${fallbackFormatted}`}</h2>
              )}
            </StyledInsightsRevenueCard>
          </Col>
          <Col xs={24} md={12} style={{display: "flex", flexDirection: "column"}}>
            <StyledInsightsSalesCard
              style={{
                backgroundColor: "#E7EDE1",
                color: "#16350F",
                borderRadius: "5px",
                border: "1.6px solid #355D2C",
              }}
            >
              <div style={{backgroundColor: "#B6C2A8"}} className="data-circle"></div>
              <p className="data-box-title">
                Total sales
                <Tooltip
                  placement="left"
                  title="Total subscription sales across all currencies"
                >
                  <Button icon={<QuestionCircleOutlined />} size="small" type="text" />
                </Tooltip>
              </p>
              <h2 className="data-box-value">{totalSales}</h2>
            </StyledInsightsSalesCard>
          </Col>
        </Row>
      </Space>
    </div>
  );
}
